import React, { useState, useEffect } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useStripe } from '@stripe/react-stripe-js'
import qs from 'query-string'
import { ThemeProvider } from '@material-ui/core/styles'
import themes, { extendTheme } from 'themes'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Container from '@material-ui/core/Container'
import Typography from '@material-ui/core/Typography'
import Divider from '@material-ui/core/Divider'
import CircularProgress from '@material-ui/core/CircularProgress'
import InfoOutlinedIcon from 'components/material-symbol/icons/Info'
import { useGetProductByPaymentIntentId, GetStudentQuery } from './query'
import { useStyles } from './styles'
import { useSnackbar } from 'notistack'
import { withStripeElements } from 'lib/stripe'
import { useQuery } from '@apollo/client'
import Error from 'components/error'
import Disclaimer from 'components/disclaimer'
import LessonPackPurchaseConfirmation from './layouts/lesson-pack-purchase-confirmation'
import PracticeSessionPurchaseConfirmation from './layouts/practice-session-purchase-confirmation'
import EnrollmentPurchaseConfirmation from './layouts/enrollment-purchase-confirmation'
import PrivateLessonPurchaseConfirmation from './layouts/private-lesson-purchase-confirmation'
import { amplitudeEvent } from 'lib/amplitude'
import { flags, useFeatureFlags } from 'lib/feature-flags'
import { groupEventRegistrationExperimentProps } from 'pages/things-to-do/events/register/registration-experiment'
import { getInitialPurchasePage, getPurchaseTypeByPathname } from './utils'
import RouterLinkButton from 'components/router-link-button'

const ProductPurchasePage = () => {
  const [requireContactPhone] = useFeatureFlags([
    flags.FLAG_EXP_GROUP_EVENT_REQUIRE_CONTACT_PHONE,
  ])
  const location = useLocation()
  const { pathname } = location
  const { payment_intent_client_secret: paymentIntentClientSecret } = qs.parse(
    location.search,
  )
  const stripe = useStripe()
  const classes = useStyles()
  const { enqueueSnackbar } = useSnackbar()
  const [paymentIntentId, setPaymentIntentId] = useState(null)
  const [purchaseStatus, setPurchaseStatus] = useState('pending')
  const { studentId, code } = useParams()

  useEffect(() => {
    let isMounted = true
    const pollInterval = 500 // 0.5 seconds
    const maxDuration = 60000 // 1 minute
    const startTime = Date.now()

    const pollPaymentIntent = async () => {
      if (!stripe || !paymentIntentClientSecret) return

      // Stop polling if 1 minute has passed
      if (Date.now() - startTime > maxDuration) {
        if (isMounted) {
          setPurchaseStatus('failed')
          enqueueSnackbar('Payment verification timed out.', {
            variant: 'warning',
          })
        }
        return
      }

      try {
        const { paymentIntent, error } = await stripe.retrievePaymentIntent(
          paymentIntentClientSecret,
        )

        if (!isMounted) return

        if (error) {
          setPurchaseStatus('failed')
          enqueueSnackbar('Unable to verify payment status', {
            variant: 'error',
          })
          return
        }

        if (paymentIntent) {
          setPaymentIntentId(paymentIntent.id)
          switch (paymentIntent.status) {
            case 'requires_capture':
              setPurchaseStatus('pending')
              break
            case 'succeeded':
              setPurchaseStatus('complete')
              enqueueSnackbar('Payment succeeded!', { variant: 'success' })
              return
            case 'processing':
              setPurchaseStatus('processing')
              break
            case 'canceled':
              setPurchaseStatus('canceled')
              return
            default:
              break
          }
          setTimeout(pollPaymentIntent, pollInterval)
        }
      } catch (err) {
        if (isMounted) {
          setPurchaseStatus('failed')
          enqueueSnackbar('Error verifying payment', { variant: 'error' })
        }
      }
    }

    pollPaymentIntent()

    return () => {
      isMounted = false
    }
  }, [stripe, paymentIntentClientSecret, enqueueSnackbar])

  const purchaseType = getPurchaseTypeByPathname(pathname)
  const isGroupEventPurchase = purchaseType === 'group-event'

  const { product, error, loading } = useGetProductByPaymentIntentId(
    paymentIntentId,
    { includeEnrollmentTransactions: !isGroupEventPurchase },
  )

  const lessonPack = product && purchaseType === 'lesson-pack' ? product : null

  const practiceSession =
    product && purchaseType === 'practice-session' ? product : null

  const enrollment =
    product &&
    (purchaseType === 'group-event' || purchaseType === 'private-lesson')
      ? product
      : null

  const isPrivateLessonPurchase = product && purchaseType === 'private-lesson'

  const {
    data: fetchedStudent,
    error: studentError,
    loading: studentLoading,
  } = useQuery(GetStudentQuery, {
    variables: {
      studentId: studentId,
    },
    skip: !studentId || isPrivateLessonPurchase,
  })

  useEffect(() => {
    if (purchaseStatus === 'complete' && product) {
      const amplitudePurchaseType =
        purchaseType === 'lesson-pack'
          ? 'lesson_pack'
          : purchaseType === 'practice-session'
            ? 'private_booking'
            : purchaseType === 'group-event'
              ? 'group_event'
              : purchaseType === 'private-lesson'
                ? 'private_lesson'
                : null

      if (amplitudePurchaseType) {
        amplitudeEvent('complete-checkout', {
          purchase_type: amplitudePurchaseType,
          ...(amplitudePurchaseType === 'group_event'
            ? groupEventRegistrationExperimentProps({ requireContactPhone })
            : {}),
        })
      }
    }
  }, [purchaseStatus, product, purchaseType, requireContactPhone])

  const heading =
    purchaseStatus === 'canceled' ? 'Purchase Not Completed' : 'Thank you!'

  const getDisclaimerDescription = ({ purchaseStatus, pathname }) => {
    const purchaseType = getPurchaseTypeByPathname(pathname)
    const isGroupEventPurchase = purchaseType === 'group-event'

    const canceledPurchaseMessage = isGroupEventPurchase
      ? 'Unable to complete your purchase. The group event selected may be full.'
      : 'Unable to complete your purchase. The selected time slot may no longer be available. Please try again or select a different time.'

    if (purchaseStatus === 'canceled') {
      return canceledPurchaseMessage
    } else {
      return 'There was an issue loading your purchase details. Please try reloading the page.'
    }
  }

  const disclaimerAction =
    purchaseStatus === 'canceled' ? (
      <RouterLinkButton
        variant="contained"
        color="primary"
        to={getInitialPurchasePage(pathname, code)}
      >
        Retry
      </RouterLinkButton>
    ) : (
      <Button
        variant="contained"
        color="primary"
        onClick={() => window.location.reload()}
      >
        Reload Page
      </Button>
    )

  if (loading || studentLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    )
  }

  return (
    <ThemeProvider theme={extendTheme(themes.offeringsRefresh)}>
      <Box display="flex" flexDirection="column" width="100%" mt={7} px={1}>
        <Container className={classes.root} maxWidth="md">
          <Box>
            <Typography variant="h4">{heading}</Typography>
            <Typography variant="body1" className={classes.fontBolded}>
              {`Your purchase is ${purchaseStatus}.`}
              {purchaseStatus === 'complete' &&
                ' You will receive a confirmation email shortly.'}
            </Typography>
          </Box>
          {(purchaseStatus === 'pending' ||
            purchaseStatus === 'processing') && <CircularProgress />}
          <Divider className={classes.divider} />
        </Container>
        {error && (
          <>
            <Box maxWidth="md" mx="auto" my={2}>
              <Disclaimer
                icon={<InfoOutlinedIcon style={{ fontSize: '32px' }} />}
                description={getDisclaimerDescription({
                  purchaseStatus,
                  pathname,
                })}
                action={disclaimerAction}
              />
            </Box>
            <Error
              error={error}
              message={error.message || 'Error loading product details'}
              showHeading={false}
            />
          </>
        )}
        {purchaseStatus === 'failed' && (
          <Error message="Payment verification failed." statusCode={500} />
        )}
        {lessonPack && (
          <LessonPackPurchaseConfirmation
            lessonPack={lessonPack}
            fetchedStudent={fetchedStudent}
            studentLoading={studentLoading}
            studentError={studentError}
          />
        )}
        {practiceSession && (
          <PracticeSessionPurchaseConfirmation
            practiceSession={practiceSession}
            fetchedStudent={fetchedStudent?.student}
          />
        )}
        {isGroupEventPurchase && enrollment && (
          <EnrollmentPurchaseConfirmation enrollment={enrollment} />
        )}
        {isPrivateLessonPurchase && enrollment && (
          <PrivateLessonPurchaseConfirmation lesson={enrollment} />
        )}
      </Box>
    </ThemeProvider>
  )
}

export default withStripeElements(ProductPurchasePage)
